/* Copyright (c) 2022-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Subject, Observable, defer, from, throwError, of, ReplaySubject } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { IDBPDatabase, openDB } from 'idb';
import { RequestSerializer } from '../../request-serializer/request-serializer';
import { DbDataStore } from './db-data-store.class';
import { DbStoreDefinitionMap, DbDataStoreMap, DbDataStoreConfig, DbItemMetadeta } from './db.models';

export class DBHandler {
    private readonly dataStores: DbDataStoreMap;
    private readonly dbItemChange$: Subject<DbItemMetadeta>;
    get onDbItemChange() {
        return this.dbItemChange$;
    }
    private dbObjectStores: DOMStringList;
    private dbInstance: IDBPDatabase;
    private readonly requestQueue: RequestSerializer<IDBPDatabase>;
    private dbObjectStoresInitialized = new ReplaySubject<boolean>(1);

    constructor(
        private dbName: string,
        private globalStoreDefMap?: DbStoreDefinitionMap,
        private userStoreDefMap?: DbStoreDefinitionMap
    ) {
        this.dataStores = new Map();
        this.dbItemChange$ = new Subject();
        if (!this.globalStoreDefMap) {
            this.globalStoreDefMap = new Map();
        }
        if (!this.userStoreDefMap) {
            this.userStoreDefMap = new Map();
        }
        this.requestQueue = new RequestSerializer(
            true,
            {
                next: undefined,
                error: this.closeDbConnection.bind(this),
                complete: this.closeDbConnection.bind(this)
            },
            this.refreshDbInstance.bind(this)
        );
    }

    private logError(...args: any[]): void {
        console.error('DbHandler', this.dbName, JSON.stringify(args));
    }

    private logInfo(...args: any[]): void {
        // eslint-disable-next-line no-console
        console.info('DbHandler', this.dbName, JSON.stringify(args));
    }

    private refreshDbInstance(storeName: string) {
        return defer(() => {
            if (this.dbObjectStores && !this.dbObjectStores.contains(storeName)) {
                return of(true);
            }
            return of(false);
        }).pipe(
            mergeMap(isUpgradeNeeded => {
                if (isUpgradeNeeded) {
                    this.logInfo('retrieving db version from disk');
                    return this.openDbInstance(storeName).pipe(
                        mergeMap(db => {
                            this.dbInstance = db;
                            this.dbObjectStores = this.dbInstance.objectStoreNames;
                            this.dbObjectStoresInitialized.next(true);
                            const version = this.dbInstance.version;
                            this.logInfo('current db version', version);
                            this.closeDbConnection();
                            return this.openDbInstance(storeName, version + 1);
                        })
                    );
                }
                return this.openDbInstance(storeName).pipe(
                    mergeMap(db => {
                        this.dbInstance = db;
                        this.dbObjectStores = this.dbInstance.objectStoreNames;
                        this.dbObjectStoresInitialized.next(true);
                        if (!this.dbObjectStores.contains(storeName)) {
                            const version = this.dbInstance.version;
                            this.logInfo(`store ${storeName} not found in current db version ${version}, upgrading`);
                            this.closeDbConnection();
                            return this.openDbInstance(storeName, version + 1);
                        }
                        return of(this.dbInstance);
                    })
                );
            }),
            catchError(err => {
                this.logError('error', err);
                return throwError(err);
            })
        );
    }

    private openDbInstance(storeName: string, version?: number): Observable<IDBPDatabase> {
        return from(
            openDB(this.dbName, version, {
                upgrade: (db: IDBPDatabase) => {
                    this.logInfo('upgrading db for', storeName);
                    this.dbInstance = db;
                    this.dbObjectStores = this.dbInstance.objectStoreNames;
                    this.dbObjectStoresInitialized.next(true);
                    if (!this.dbObjectStores.contains(storeName)) {
                        this.dbInstance.createObjectStore(storeName);
                    }
                }
            })
        );
    }

    private closeDbConnection(): void {
        if (this.dbInstance) {
            this.dbInstance.close();
            this.dbInstance = null;
        } else {
            this.logError('attempted to close db connection when local instance unavailable');
        }
    }

    getDataStore(config: DbDataStoreConfig): DbDataStore {
        if (!this.dataStores.has(config.storeDbName)) {
            this.logInfo(`Create datastore instance: ${config.storeDbName}`);
            const dataStoreConfig = Object.assign({}, config, {
                dbName: this.dbName
            });
            this.dataStores.set(
                config.storeDbName,
                new DbDataStore(dataStoreConfig, this.dbItemChange$, this.requestQueue)
            );
        }

        return this.dataStores.get(config.storeDbName);
    }

    /**
     * Returns an interface to a global data store. The interface exposes the same API
     * as localforage, but each call is wrapped in an observable that gets resolved when the upgrade
     * process has been completed.
     *
     * @example
     * dbService.getGlobalStore('myStoreName').getItem('key').subscribe((value) => {
     *     console.log(value);
     * });
     * dbService.getGlobalStore('myStoreName').setItem('key', value).subscribe((value) => {
     *     console.log('setItem has completed');
     * });
     */
    getGlobalStore(storeName: string): DbDataStore {
        return this.getDataStore({
            storeName,
            storeDbName: storeName,
            storeDefinition: this.globalStoreDefMap.get(storeName)
        });
    }

    /**
     * Returns an interface to a local data store.
     *
     * @see {@link getGlobalStore}
     */
    getUserStore(userId: string, storeName: string): DbDataStore {
        const storeDbName = `${userId}_${storeName}`;
        return this.getDataStore({
            storeName,
            storeDbName,
            userId,
            storeDefinition: this.userStoreDefMap.get(storeName)
        });
    }

    /**
     * Returns a raw interface to an unmanaged localforage store. No upgrade process is used.
     * Useful for a backup mirror database
     */
    getStore(storeName: string): DbDataStore {
        return this.getDataStore({
            storeName,
            storeDbName: storeName
        });
    }

    checkIfStoreExists(storeName: string): Observable<boolean> {
        return this.dbObjectStoresInitialized.asObservable().pipe(map(() => this.dbObjectStores.contains(storeName)));
    }

    getAllStoreNames(): string[] {
        return Array.from(this.dataStores.keys());
    }
}
